import { useEffect, useState } from "react";
import { Share2, CheckCircle2, Loader2, Plug, Unplug, Eye, EyeOff } from "lucide-react";
import { api, fmtError } from "../lib/api";
import { toast } from "sonner";

export const AdminMeta = () => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ app_id: "", app_secret: "", page_token: "" });
  const [showSecrets, setShowSecrets] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/meta/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));

  useEffect(() => {
    load();
  }, []);

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/meta/connect", form);
      toast.success(`Connecté à la page « ${data.page_name} »${data.instagram ? ` + Instagram @${data.instagram}` : ""}`);
      setForm({ app_id: "", app_secret: "", page_token: "" });
      load();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleAuto = async () => {
    try {
      await api.patch("/admin/meta", { auto_publish: !status.auto_publish });
      setStatus({ ...status, auto_publish: !status.auto_publish });
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter Meta ? Les codes seront effacés du site et la publication automatique s'arrêtera.")) return;
    try {
      await api.delete("/admin/meta");
      toast.success("Meta déconnecté");
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const refreshIg = async () => {
    try {
      const { data } = await api.post("/admin/meta/refresh-ig");
      toast.success(`Instagram @${data.instagram} détecté et lié`);
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500";
  const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500";

  if (!status) return null;

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="admin-meta-section">
      <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
        <Share2 className="h-5 w-5 text-amber-500" /> Publication automatique
      </h2>

      {status.connected ? (
        <div className="mt-5 space-y-4">
          <p className="flex items-center gap-2 text-sm text-emerald-400" data-testid="meta-connected">
            <CheckCircle2 className="h-4 w-4" />
            Connecté à la page « {status.page_name} »
            {status.instagram
              ? ` — Instagram @${status.instagram} lié`
              : " — aucun compte Instagram pro lié à la page"}
          </p>
          <label className="flex cursor-pointer items-center gap-3 text-sm text-zinc-300" data-testid="meta-auto-toggle">
            <input
              type="checkbox"
              checked={status.auto_publish}
              onChange={toggleAuto}
              className="h-4 w-4 accent-amber-500"
            />
            Publier automatiquement chaque nouveau morceau sur Facebook{status.instagram ? " et Instagram" : ""}
          </label>
          <button
            onClick={disconnect}
            data-testid="meta-disconnect"
            className="flex items-center gap-2 border border-red-500/40 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-red-400 transition-colors hover:bg-red-500 hover:text-black"
          >
            <Unplug className="h-4 w-4" /> Déconnecter
          </button>
          {!status.instagram && (
            <div className="border border-amber-500/30 bg-amber-500/5 p-4">
              <p className="text-xs leading-relaxed text-zinc-400">
                Pour Instagram : liez d'abord votre compte Instagram professionnel à la page
                (app Instagram → votre profil → « Modifier le profil » → « Page » → La Console des Artistes),
                puis cliquez ci-dessous.
              </p>
              <button
                onClick={refreshIg}
                data-testid="meta-refresh-ig"
                className="mt-3 border border-amber-500/60 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
              >
                Redétecter Instagram
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            Collez ici les codes de votre app Meta : ils transitent uniquement entre votre navigateur
            et ce serveur, sont stockés de façon sécurisée et ne seront jamais affichés.
            Le site vérifie la connexion immédiatement.
          </p>
          <form onSubmit={connect} className="mt-5 space-y-4" data-testid="meta-connect-form">
            <div>
              <label className={labelCls}>ID de l'app</label>
              <input required value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })}
                     data-testid="meta-app-id" className={inputCls} placeholder="Ex. 1037053145892079" />
            </div>
            <div>
              <label className={labelCls}>Clé secrète de l'app</label>
              <div className="relative">
                <input required type={showSecrets ? "text" : "password"} value={form.app_secret}
                       onChange={(e) => setForm({ ...form, app_secret: e.target.value })}
                       data-testid="meta-app-secret" className={inputCls} placeholder="Paramètres → Général → Afficher" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Token d'accès (EAA…)</label>
              <div className="relative">
                <input required type={showSecrets ? "text" : "password"} value={form.page_token}
                       onChange={(e) => setForm({ ...form, page_token: e.target.value })}
                       data-testid="meta-page-token" className={inputCls}
                       placeholder="Explorateur d'API Graph → Générer un token d'accès" />
                <button type="button" onClick={() => setShowSecrets((s) => !s)} data-testid="meta-toggle-visibility"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-400"
                        title="Afficher/masquer">
                  {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit" disabled={busy} data-testid="meta-connect-submit"
              className="flex items-center gap-2 bg-amber-500 px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
              {busy ? "Vérification en cours…" : "Connecter et vérifier"}
            </button>
          </form>
        </>
      )}
    </section>
  );
};
